/**
 * Encounter Service
 *
 * API service for fetching encounters from external EHR systems
 */

import axios from 'axios';
import { apiService } from './api';
import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../config/api';
import { Encounter } from '../types/encounter';

export interface EncounterListResponse {
  encounters: Encounter[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class EncounterService {
  private baseUrl = `${API_BASE_URL}/v1`;

  /**
   * Get encounter by ID
   */
  async getEncounterById(encounterId: string): Promise<Encounter> {
    try {
      const response = await apiService.get<any>(
        `/v1/encounters/${encounterId}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error('Failed to fetch encounter');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Callers show Access Denied for a 403 or 404 and need the status to tell
        if (error.response?.status === 404 || error.response?.status === 403) {
          throw error;
        }
        throw new Error(error.response?.data?.message || 'Failed to fetch encounter');
      }
      throw error;
    }
  }

  /**
   * Get encounters with filters
   */
  async getEncounters(params?: {
    matchStatus?: 'matched' | 'unmatched' | 'pending';
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    page?: number;
    search?: string;
    type?: string;
    status?: string;
    externalSystemId?: string;
  }): Promise<EncounterListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.matchStatus) {
        queryParams.append('matchStatus', params.matchStatus);
      }
      if (params?.dateFrom) {
        queryParams.append('dateFrom', params.dateFrom);
      }
      if (params?.dateTo) {
        queryParams.append('dateTo', params.dateTo);
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.type) {
        queryParams.append('type', params.type);
      }
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.externalSystemId) {
        queryParams.append('externalSystemId', params.externalSystemId);
      }

      const path = `/v1/encounters${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await apiService.get<any>(path);

      if (response.success && Array.isArray(response.data)) {
        return {
          encounters: response.data,
          pagination: response.pagination || {
            page: params?.page || 1,
            limit: params?.limit || 20,
            total: response.data.length,
            pages: 1
          }
        };
      }

      return {
        encounters: [],
        pagination: {
          page: 1,
          limit: params?.limit || 20,
          total: 0,
          pages: 0
        }
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch encounters');
      }
      throw error;
    }
  }

  /**
   * Update encounter status
   */
  async updateEncounterStatus(
    encounterId: string,
    status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled'
  ): Promise<Encounter> {
    try {
      const response = await axios.patch(
        `${this.baseUrl}/encounters/${encounterId}/status`,
        { status },
        {
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data.encounter;
      }

      throw new Error('Failed to update encounter status');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(error.response?.data?.error || 'Invalid status transition');
        }
        if (error.response?.status === 403) {
          throw new Error('You do not have permission to update this encounter');
        }
        if (error.response?.status === 404) {
          throw new Error('Encounter not found');
        }
        throw new Error(error.response?.data?.error || 'Failed to update encounter status');
      }
      throw error;
    }
  }
}

export const encounterService = new EncounterService();
